// Mithril global.

/**
 * The footer text, with a small SAFE subset of HTML honoured.
 *
 * Admins paste things like `<br />©2026: A WVCS Website` into the footer text,
 * and it rendered literally — the tag showed on the page. But an admin must not
 * be able to break their own site, so this is NOT innerHTML. The raw string is
 * scanned for a fixed list of tags, each rebuilt as a Mithril vnode with only
 * the attributes listed here; everything else stays text, which Mithril
 * escapes, so `<script>`, `<img onerror>`, `<div style>` and friends show as
 * the literal characters the admin typed.
 *
 * Honoured:
 *   - line breaks: a newline, or <br>, <br/>, <br />
 *   - links: <a href="…"> with an http(s): or mailto: target only, optionally
 *     target="_blank" (always given rel="noopener noreferrer"), title allowed
 *   - <strong>/<b>, <em>/<i>, and <span> with NO attributes
 *   - entities: &copy; &reg; &trade; &amp; &nbsp; dashes, quotes, numeric …
 *
 * A tag carrying any other attribute (style, class, onclick…) is not honoured
 * at all and shows as typed, so nothing half-works silently. A closing tag
 * with no matching opener shows as typed; an opener left unclosed is closed at
 * the end of the text.
 */

const NAMED = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  copy: '©',
  reg: '®',
  trade: '™',
  mdash: '—',
  ndash: '–',
  middot: '·',
  bull: '•',
  hellip: '…',
  laquo: '«',
  raquo: '»',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  deg: '°',
  times: '×',
  euro: '€',
  pound: '£',
  sect: '§',
  para: '¶',
  hearts: '♥',
  star: '☆'
};

/** Decode the entities above and numeric ones; anything unknown stays as typed. */
export function decodeEntities(s) {
  return s.replace(/&(#x[0-9a-f]{1,6}|#[0-9]{1,7}|[a-z]{2,8});/gi, (whole, body) => {
    if (body[0] === '#') {
      const cp = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      // No control characters, no surrogates, nothing past Unicode.
      const ok = cp >= 0x20 && cp <= 0x10ffff && !(cp >= 0x7f && cp <= 0x9f) && !(cp >= 0xd800 && cp <= 0xdfff);
      return ok ? String.fromCodePoint(cp) : whole;
    }
    const v = NAMED[body.toLowerCase()];
    return v === undefined ? whole : v;
  });
}
const SIMPLE = {
  strong: 'strong',
  b: 'strong',
  em: 'em',
  i: 'em',
  span: 'span'
};

/** Parse `name="value"` pairs. Returns null if anything else is in there. */
function parseAttrs(src) {
  const out = {};
  const re = /\s*([a-z][a-z-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))\s*/giy;
  let pos = 0;
  const rest = src.replace(/\s*\/?\s*$/, '');
  while (pos < rest.length) {
    re.lastIndex = pos;
    const mm = re.exec(rest);
    if (!mm || mm.index !== pos) return null;
    out[mm[1].toLowerCase()] = mm[2] ?? mm[3] ?? mm[4] ?? '';
    pos = re.lastIndex;
  }
  return out;
}
function safeHref(raw) {
  const href = decodeEntities(raw).trim();
  return /^(https?:\/\/[^\s]+|mailto:[^\s]+)$/i.test(href) ? href : null;
}
function pushText(into, text) {
  if (!text) return;
  decodeEntities(text).split(/\r\n|\r|\n/).forEach((part, i) => {
    if (i > 0) into.push(m('br'));
    if (part) into.push(part);
  });
}

/** The footer text as an array of Mithril children. */
export default function safeFooterText(raw) {
  const root = {
    tag: '',
    attrs: {},
    children: []
  };
  const stack = [root];
  const top = () => stack[stack.length - 1];
  const tagRe = /<(\/?)([a-z]+)((?:\s[^<>]*)?)\s*(\/?)>/gi;
  let last = 0;
  let mm;
  while (mm = tagRe.exec(raw)) {
    const [whole, closing, nameRaw, attrSrc] = mm;
    const name = nameRaw.toLowerCase();
    let handled = false;
    if (!closing && name === 'br' && !attrSrc.trim().replace(/\/$/, '').trim()) {
      pushText(top().children, raw.slice(last, mm.index));
      top().children.push(m('br'));
      handled = true;
    } else if (!closing && SIMPLE[name] && !attrSrc.trim()) {
      pushText(top().children, raw.slice(last, mm.index));
      stack.push({
        tag: name,
        attrs: {},
        children: []
      });
      handled = true;
    } else if (!closing && name === 'a') {
      const attrs = parseAttrs(attrSrc);
      const allowed = attrs && Object.keys(attrs).every(k => k === 'href' || k === 'target' || k === 'title' || k === 'rel');
      const href = attrs && attrs.href !== undefined ? safeHref(attrs.href) : null;
      const target = attrs && attrs.target !== undefined ? attrs.target.toLowerCase() : null;
      const targetOk = target === null || target === '_blank' || target === '_self';
      // A link inside a link is not valid HTML; the inner one stays as text.
      const nested = stack.some(n => n.tag === 'a');
      if (attrs && allowed && href && targetOk && !nested) {
        pushText(top().children, raw.slice(last, mm.index));
        const a = {
          href,
          rel: 'noopener noreferrer'
        };
        if (target === '_blank') a.target = '_blank';
        if (attrs.title) a.title = decodeEntities(attrs.title);
        stack.push({
          tag: 'a',
          attrs: a,
          children: []
        });
        handled = true;
      }
    } else if (closing && !attrSrc.trim() && stack.some((n, i) => i > 0 && n.tag === name)) {
      // Closes the nearest open element of that name, and anything opened
      // inside it — `<b><i>x</b>` ends both, as a browser would.
      pushText(top().children, raw.slice(last, mm.index));
      let node;
      do {
        node = stack.pop();
        top().children.push(m(SIMPLE[node.tag] || node.tag, node.attrs, node.children));
      } while (node.tag !== name);
      handled = true;
    }
    if (handled) {
      last = mm.index + whole.length;
    }
    // Not handled: the tag stays in the text and is shown as typed.
  }
  pushText(top().children, raw.slice(last));

  // Close anything left open.
  while (stack.length > 1) {
    const node = stack.pop();
    top().children.push(m(SIMPLE[node.tag] || node.tag, node.attrs, node.children));
  }
  return root.children;
}
flarum.reg.add('ernestdefoe-bespoke', 'forum/util/safeFooterText', safeFooterText);